import Cookies from "js-cookie";

/**
 * The one place the session token is read from and written to.
 *
 * It used to live in two stores at once — a cookie that `useApi` and the auth redirect read, and
 * a `localStorage.accessToken` that RTK Query's `baseApi` read — written in two places and
 * cleared in a third. Two sources of truth for "is there a session" is a drift bug waiting to
 * happen, so the cookie is now the only store and this module is the only door to it.
 *
 * Non-React on purpose: `baseApi`'s `prepareHeaders` and axios interceptors run outside the tree
 * and cannot use a hook. `useAuthToken` wraps this for the components that need to re-render
 * when the token changes.
 */
const TOKEN_COOKIE = "token";

/**
 * `sameSite: "strict"` because the app never expects a cross-site request to carry the session,
 * and `secure` off localhost so a dev server on plain http still works.
 */
const cookieAttributes = (): Cookies.CookieAttributes => ({
  path: "/",
  sameSite: "strict",
  secure: window.location.protocol === "https:",
});

export const readAuthToken = () => Cookies.get(TOKEN_COOKIE);

/**
 * `expiresAt` is the JWT's own expiry. Without it the cookie is a session cookie, which is what
 * "remember me" unchecked means: gone when the browser closes.
 */
export const writeAuthToken = (token: string, expiresAt?: Date) => {
  Cookies.set(TOKEN_COOKIE, token, { ...cookieAttributes(), expires: expiresAt });
};

// The attributes have to match the ones the cookie was written with, or the browser removes
// nothing and the session survives a logout.
export const clearAuthToken = () => Cookies.remove(TOKEN_COOKIE, cookieAttributes());
